// src/components/BlogSection.js
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Calendar, User } from 'react-feather';

const BlogSection = () => {
  const [blogs, setBlogs] = useState([]);
  const imageBaseUrl = 'https://dawamaxma.dawamtaani.co.ke';

  useEffect(() => {
    fetch('https://dawamaxma.dawamtaani.co.ke/api/blogs')
      .then((response) => response.json())
      .then((data) => setBlogs(data))
      .catch((error) => console.error('Error fetching blogs:', error));
  }, []);

  const truncateContent = (content, maxLength = 120) => {
    return content.length > maxLength ? content.slice(0, maxLength) + '...' : content;
  };

  // Get the latest three blogs
  const latestBlogs = blogs.slice(0, 3);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  return (
    <section className="bg-white py-24">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-between items-end mb-12 gap-6">
          <div className="max-w-2xl">
            <h2 className="text-primary-600 font-bold tracking-wide uppercase text-sm mb-3">Health Education</h2>
            <h3 className="text-4xl md:text-5xl font-extrabold text-neutral-900 mb-4">
              Latest from our <span className="text-primary-600">Journal</span>
            </h3>
            <p className="text-lg text-neutral-600">
              Expert advice, wellness tips and medical insights to help you lead a healthier lifestyle.
            </p>
          </div>
          <Link
            to="/blogs"
            className="hidden md:flex items-center text-primary-600 font-bold hover:text-primary-700 transition-colors group"
          >
            View All Articles
            <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
          </Link>
        </div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {latestBlogs.map((blog) => {
            const fullImageUrl = blog.image ? `${imageBaseUrl}/${blog.image}` : 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?q=80&w=800';

            return (
              <motion.div
                key={blog.id}
                variants={itemVariants}
                className="group flex flex-col bg-white rounded-3xl overflow-hidden border border-neutral-100 shadow-sm hover:shadow-xl transition-all duration-300"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={fullImageUrl}
                    alt={blog.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute top-4 left-4">
                    <span className="bg-white/90 backdrop-blur-md text-primary-600 text-[10px] font-bold px-3 py-1.5 rounded-full uppercase tracking-wider shadow-sm">
                      Health Tips
                    </span>
                  </div>
                </div>

                <div className="p-8 flex flex-col flex-1">
                  <div className="flex items-center text-xs text-neutral-400 mb-4 space-x-4">
                    <div className="flex items-center">
                      <Calendar className="w-3.5 h-3.5 mr-1.5 text-primary-400" />
                      {new Date(blog.created_at || Date.now()).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </div>
                    <div className="flex items-center">
                      <User className="w-3.5 h-3.5 mr-1.5 text-primary-400" />
                      Admin
                    </div>
                  </div>

                  <h4 className="text-xl font-bold text-neutral-900 mb-3 group-hover:text-primary-600 transition-colors line-clamp-2">
                    {blog.title}
                  </h4>
                  <p className="text-neutral-600 mb-6 flex-1 line-clamp-3">
                    {truncateContent(blog.subheading || blog.content)}
                  </p>

                  <div className="pt-6 border-t border-neutral-50">
                    <Link
                      to={`/blogs/${blog.id}`}
                      className="inline-flex items-center text-primary-600 font-bold group/btn"
                    >
                      Read Article
                      <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover/btn:translate-x-2" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        <div className="mt-12 text-center md:hidden">
          <Link
            to="/blogs"
            className="inline-flex items-center bg-primary-50 text-primary-600 px-8 py-3 rounded-full font-bold hover:bg-primary-100 transition-all"
          >
            View All Articles
            <ArrowRight className="ml-2 w-5 h-5" />
          </Link>
        </div>
      </div>
    </section>
  );
};

export default BlogSection;
